<script setup lang="ts">
  import {
    CustomersService,
    BalanceAdjustmentType,
  } from '@/api/services/customers.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { computed, ref, getCurrentInstance } from 'vue';
  import ModalPay from '../pay/ModalPay.vue';
  import { PaymentType } from '@/enums/PaymentEnums';
  import { PaymentMethod } from '@/enums/PaymentMethod';

  const modals = useModal();
  const ui = useUi();

  const props = defineProps<{
    customer: ICustomer;
  }>();

  const { proxy } = getCurrentInstance();

  const adjustType = ref<BalanceAdjustmentType | null>(null);
  const adjustAmount = ref<string>('');
  const note = ref<string>('');

  const adjustAmountTitle = computed(() => {
    if (adjustType.value === BalanceAdjustmentType.Add) {
      return 'Monto a agregar';
    } else if (adjustType.value === BalanceAdjustmentType.Subtract) {
      return 'Monto a restar';
    } else if (adjustType.value === BalanceAdjustmentType.Set) {
      return 'Nuevo saldo';
    }
  });

  const isValid = computed(() => {
    return adjustType.value && +(adjustAmount.value || 0) > 0;
  });

  const saving = ref(false);
  async function save() {
    if (!isValid.value || saving.value) return;

    try {
      saving.value = true;
      const amount = +(adjustAmount.value || 0);

      const isAdding =
        adjustType.value === BalanceAdjustmentType.Add ||
        (adjustType.value === BalanceAdjustmentType.Set &&
          amount > props.customer.balance);

      if (isAdding) {
        const addAmount =
          adjustType.value === BalanceAdjustmentType.Add
            ? amount
            : amount - props.customer.balance;

        showPaymentDialog(addAmount);
        return;
      }

      await CustomersService.adjustBalance({
        id: props.customer.id,
        amount: amount,
        type: adjustType.value!,
        note: note.value,
      });

      ui.showToast(AlertType.Success, 'Saldo ajustado correctamente');
      modals.hide('customer-balance');
      proxy.$parent.$emit('saved');
    } catch (error) {
      ui.serverError(error, 'Error al ajustar el saldo');
    } finally {
      saving.value = false;
    }
  }

  async function showPaymentDialog(toAdd: number) {
    modals.show(
      ModalPay as any,
      {
        //order: order.value,
        type: PaymentType.CustomerBalance,
        unpaidAmount: toAdd,
        entityIds: [props.customer.id],
        hiddenMethods: [
          PaymentMethod.CustomerAccount,
          PaymentMethod.Employee,
          PaymentMethod.Invitation,
          PaymentMethod.Club,
        ],
      },
      {
        name: 'customer-balance-pay',
        //classes: 'dialog--height-limit',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      },
      {
        paid: (amount: number, remainingToPay: number) => {
          if (remainingToPay <= 0) {
            modals.hide('customer-balance');
            proxy.$parent.$emit('saved');
          }
        },
      }
    );
  }
</script>

<template>
  <v-card class="modal-customer-balance">
    <v-card-title>
      <span>Saldo de {{ customer.name }}</span>
    </v-card-title>
    <v-card-text>
      <div class="modal-body">
        <template v-if="!adjustType">
          <div class="adjust-type-buttons">
            <v-btn color="info" @click="adjustType = BalanceAdjustmentType.Add">
              <v-icon left>mdi-plus</v-icon>
              <span>Agregar</span>
            </v-btn>
            <v-btn
              color="info"
              @click="adjustType = BalanceAdjustmentType.Subtract"
            >
              <v-icon left>mdi-minus</v-icon>
              <span>Restar</span>
            </v-btn>
            <!-- <v-btn color="info" @click="adjustType = BalanceAdjustmentType.Set">
              <v-icon left>mdi-adjust</v-icon>
              <span>Establecer</span>
            </v-btn> -->
          </div>
        </template>
        <template v-else>
          <div class="adjust-main">
            <div class="current-balance">
              <span class="current-balance--label">Saldo actual</span>
              <span class="current-balance--value"
                >${{ customer.balance.toFixed(2) }}</span
              >
            </div>
            <div class="adjust-input">
              <span class="adjust-input--label">{{ adjustAmountTitle }}</span>
              <v-text-field
                v-model="adjustAmount"
                type="number"
                dense
                placeholder="0"
                prefix="$"
                autofocus
                autocomplete="off"
              />
            </div>
          </div>
          <!-- <div class="movement-note">
            <span class="movement-note--label">Nota (opcional)</span>
            <v-text-field
              v-model="note"
              dense
              placeholder="Nota sobre el ajuste"
              autocomplete="off"
            />
          </div> -->
        </template>
      </div>
    </v-card-text>
    <v-card-actions>
      <div class="tw-w-full tw-flex tw-justify-center tw-space-x-2">
        <v-btn
          color="grey"
          text
          @click="modals.hide('customer-balance')"
          :disabled="saving"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar</v-btn
        >
        <v-btn
          v-if="adjustType"
          color="primary"
          :disabled="!isValid"
          @click="save"
          :loading="saving"
        >
          <v-icon left>mdi-check</v-icon>
          Guardar</v-btn
        >
      </div>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-customer-balance {
    .modal-body {
      @apply tw-p-0;

      .adjust-type-buttons {
        @apply tw-flex tw-justify-between tw-space-x-2;

        .v-btn {
          @apply tw-flex-1 tw-h-16;
        }
      }

      .adjust-main {
        @apply tw-flex tw-justify-between tw-space-x-2;

        .current-balance {
          @apply tw-flex tw-flex-col tw-space-y-0;

          &--label {
            @apply tw-text-gray-500;
          }

          &--value {
            @apply tw-text-2xl tw-font-bold;
          }
        }

        .adjust-input {
          &--label {
            @apply tw-font-bold tw-text-black;
          }
        }
      }
    }
  }
</style>
